import React, { useEffect, useCallback, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { Send, CheckCircle2, ArrowLeft, Calendar, Clock, Zap, FileSpreadsheet, Phone } from "lucide-react";
import { Button } from "../../components/tailwind/button";
import { Card } from "../../components/tailwind/card";
import { RecipientInput } from "../../components/whatsapp/send-message/RecipientInput";
import { TemplateSelector } from "../../components/whatsapp/send-message/TemplateSelector";
import { hasAccessFeature, hasActivePlan } from "../../utils/capabilitiesHelper";
import { TemplatePreview } from "../../components/whatsapp/templates/TemplatePreview";
import { SendTemplateMediaUploader } from "../../components/whatsapp/send-message/SendTemplateMediaUploader";
import WhatsAppConnectionNotice from "../../components/whatsapp/WhatsAppConnectionNotice";
import SubscriptionNotice from "../../components/whatsapp/SubscriptionNotice";
import { loadTemplatesRequest } from "../../store/whatsappSlice";
import {
  setTemplate, setManualNumbers, setImportData, clearImport, createCampaignRequest, setTemplateParams, clearSendMedia,
  setCsvColumns, setCsvData, setVariableMapping, setPhoneColumn, resetForm
} from "../../store/slices/sendMessageSlice.js";
import { createCampaignScheduleRequest, resetCreateState } from "../../store/slices/campaignsSlice";
import { showAlert } from "../../store/alertSlice";
import { usePageTitle } from "../../hooks/usePageTitle";
import { extractTemplateVariables } from "../../components/whatsapp/templates/TemplateUtils";
import { buildCsv } from "./sendMessageUtils";
import VariableMappingFields from "./components/VariableMappingFields";
import { whatsappPageShell, waShell } from "./utils/whatsappPageShell";
import { WhatsAppPageIntro } from "./components/WhatsAppPageIntro";

export default function SendMessage({ mode }) {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();

  // Determine mode (campaign vs instant send message)
  const isCampaignMode = mode === 'campaign' || location.pathname.includes('/campaigns/new');

  usePageTitle(isCampaignMode ? 'New Campaign' : 'Send Message');

  // Redux states
  const {
    selectedTemplateId, manualNumbers, importedRecipients, importSource, importCsv, importCsvFileName, templateParams, sendMediaFile, status,
    csvColumns, csvData, variableMapping, phoneColumn
  } = useSelector((state) => state.sendMessage);

  const { creating, createSuccess } = useSelector((state) => state.campaigns);
  
  const rawTemplates = useSelector((state) => state.whatsapp.templates) || [];
  const templates = useMemo(() => {
    return rawTemplates.filter((t) => String(t.status || "").toLowerCase() === "approved");
  }, [rawTemplates]);
  const wabaAccounts = useSelector((state) => state.whatsapp.wabaAccounts) || [];
  const capabilities = useSelector((state) => state.capabilities.capabilities);
  
  const hasWabaId = Array.isArray(wabaAccounts) && wabaAccounts.length > 0 && wabaAccounts[0]?.wabaId;
  const hasActivePlanValue = hasActivePlan(capabilities);
  const hasSendMessageAccess = hasAccessFeature(capabilities, 'whatsapp.message.send');
  const enableSubscriptionLock = process.env.REACT_APP_ENABLE_SUBSCRIPTION_LOCK === 'true';
  const shouldLockPage = enableSubscriptionLock && (!hasActivePlanValue || !hasSendMessageAccess);

  // Local Campaign Form States
  const [campaignName, setCampaignName] = useState("");
  const [scheduleMode, setScheduleMode] = useState("now"); // now | later
  const [scheduleDate, setScheduleDate] = useState("");
  const [scheduleTime, setScheduleTime] = useState("");

  useEffect(() => {
    dispatch(resetForm());
    dispatch(loadTemplatesRequest({ loadAll: true }));
    return () => {
      if (isCampaignMode) {
        dispatch(resetCreateState());
      }
    };
  }, [dispatch, isCampaignMode]);

  const selectedTemplate = templates.find(t => String(t.id) === String(selectedTemplateId));
  const isAuthCopyCode = selectedTemplate?.category?.toLowerCase() === 'authentication' && (selectedTemplate?.otpType || 'copy_code') === 'copy_code';

  // Detect header type
  const getHeaderType = () => {
    if (!selectedTemplate?.components?.HEADER) return 'none';
    try {
      const headerComponent = typeof selectedTemplate.components.HEADER === 'string'
        ? JSON.parse(selectedTemplate.components.HEADER)
        : selectedTemplate.components.HEADER;
      const format = headerComponent?.format?.toLowerCase();
      if (['image', 'video', 'document', 'audio', 'text'].includes(format)) return format;
    } catch (e) {
      return selectedTemplate?.headerText ? 'text' : 'none';
    }
    return 'none';
  };

  const headerType = getHeaderType();
  const needsMediaUpload = ['image', 'video', 'document', 'audio'].includes(headerType);

  useEffect(() => {
    if (!selectedTemplateId) {
      dispatch(clearSendMedia());
    }
  }, [selectedTemplateId, dispatch]);

  // Extract variables
  const headerVariables = useMemo(() => {
    return extractTemplateVariables(selectedTemplate?.headerText);
  }, [selectedTemplate?.headerText]);

  const bodyVariables = useMemo(() => {
    return extractTemplateVariables(selectedTemplate?.body);
  }, [selectedTemplate?.body]);

  const buttonVariables = useMemo(() => {
    if (!selectedTemplate?.components?.BUTTONS) return [];
    try {
      const buttonsComponent = typeof selectedTemplate.components.BUTTONS === 'string'
        ? JSON.parse(selectedTemplate.components.BUTTONS)
        : selectedTemplate.components.BUTTONS;
      const buttons = buttonsComponent?.buttons || [];
      const variables = [];
      buttons.forEach((button, index) => {
        if (button.type === 'URL' && button.url) {
          const regex = /\{\{(\d+)\}\}/g;
          const matches = [...button.url.matchAll(regex)];
          matches.forEach(match => {
            variables.push({
              buttonIndex: index + 1,
              paramIndex: parseInt(match[1]),
              key: `button_url_${index + 1}_param_${match[1]}`
            });
          });
        }
      });
      return variables;
    } catch (e) {
      return [];
    }
  }, [selectedTemplate?.components?.BUTTONS]);

  const allVariables = useMemo(
    () => [...headerVariables, ...bodyVariables, ...buttonVariables.map(v => v.key), ...(isAuthCopyCode ? ['code'] : [])],
    [headerVariables, bodyVariables, buttonVariables, isAuthCopyCode]
  );

  const isImportingCsvExcel = (importSource === 'CSV' || importSource === 'EXCEL') && importedRecipients.length > 0;
  
  const allVariablesFilled = allVariables.length > 0 ? (
    isImportingCsvExcel
      ? headerVariables.every(v => variableMapping[v]) &&
        bodyVariables.every(v => variableMapping[v]) &&
        buttonVariables.every(v => variableMapping[v.key]) &&
        (!isAuthCopyCode || variableMapping['code'])
      : (
        headerVariables.every(v => templateParams.header[v]?.trim()) &&
        bodyVariables.every(v => templateParams.body[v]?.trim()) &&
        buttonVariables.every(v => templateParams.button[v.key]?.trim()) &&
        (!isAuthCopyCode || templateParams.code?.trim())
      )
  ) : true;

  useEffect(() => {
    const newParams = {
      header: headerVariables.reduce((acc, v) => ({ ...acc, [v]: templateParams.header?.[v] ?? '' }), {}),
      body: bodyVariables.reduce((acc, v) => ({ ...acc, [v]: templateParams.body?.[v] ?? '' }), {}),
      button: buttonVariables.reduce((acc, v) => ({ ...acc, [v.key]: templateParams.button?.[v.key] ?? '' }), {}),
      code: isAuthCopyCode ? templateParams.code ?? '' : undefined
    };
    dispatch(setTemplateParams(newParams));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTemplateId, templates, isAuthCopyCode]);

  useEffect(() => {
    if (csvData.length > 0 && Object.keys(variableMapping).length > 0) {
      const firstRow = csvData[0];
      const newParams = { header: {}, body: {}, button: {} };
      headerVariables.forEach(v => {
        const col = variableMapping[v];
        newParams.header[v] = col && firstRow[col] ? String(firstRow[col]) : '';
      });
      bodyVariables.forEach(v => {
        const col = variableMapping[v];
        newParams.body[v] = col && firstRow[col] ? String(firstRow[col]) : '';
      });
      buttonVariables.forEach(v => {
        const col = variableMapping[v.key];
        newParams.button[v.key] = col && firstRow[col] ? String(firstRow[col]) : '';
      });
      if (isAuthCopyCode) {
        const col = variableMapping['code'];
        newParams.code = col && firstRow[col] ? String(firstRow[col]) : '';
      }
      dispatch(setTemplateParams(newParams));
    }
  }, [variableMapping, csvData, headerVariables, bodyVariables, buttonVariables, isAuthCopyCode, dispatch]);

  const manualCount = manualNumbers ? manualNumbers.split(/[,\n]/).filter(n => n.trim()).length : 0;
  const totalRecipients = (importedRecipients && importedRecipients.length > 0) ? importedRecipients.length : manualCount;

  // Form validity check
  const isFormValid = selectedTemplateId && 
    totalRecipients > 0 && 
    allVariablesFilled && 
    (!needsMediaUpload || sendMediaFile) && 
    (!selectedTemplate?.status || selectedTemplate.status.toLowerCase() === 'approved') && 
    (isImportingCsvExcel || manualCount <= 1) &&
    (!isCampaignMode || (campaignName.trim() && (scheduleMode === "now" || (scheduleMode === "later" && scheduleDate && scheduleTime))));

  // Status mapping
  const isSending = isCampaignMode ? creating : (status === 'sending');
  const isSuccess = isCampaignMode ? createSuccess : (status === 'success');

  const handleSend = () => {
    if (needsMediaUpload && !sendMediaFile) {
      dispatch(showAlert({ message: 'Please upload media for this template', type: 'error' }));
      return;
    }

    if (!isImportingCsvExcel && manualCount > 1) {
      dispatch(showAlert({ 
        message: 'Only a single recipient is allowed when using manual entry. To send to multiple users, please use a CSV or Excel file.', 
        type: 'warning' 
      }));
      return;
    }

    let scheduledAt = null;
    let executionMode = "NOW";

    if (isCampaignMode && scheduleMode === "later") {
      const dateTimeStr = `${scheduleDate}T${scheduleTime}`;
      scheduledAt = new Date(dateTimeStr).getTime();
      if (isNaN(scheduledAt)) {
        dispatch(showAlert({ message: "Invalid date/time selected", type: "error" }));
        return;
      }
      executionMode = "SCHEDULED";
    }

    const manualPhones = manualNumbers ? manualNumbers.split(/[,\n]/).map(n => n.trim()).filter(n => n) : [];
    let modeType = 'NONE';
    if (importCsv) modeType = 'IMPORT_CSV';
    else if ((importSource === 'CSV' || importSource === 'EXCEL') && importedRecipients && importedRecipients.length > 0 && !importCsv) modeType = 'IMPORT_MISSING_CSV';
    else if (manualPhones && manualPhones.length > 0) modeType = 'MANUAL';

    if (modeType === 'IMPORT_MISSING_CSV') {
      dispatch(showAlert({ message: 'CSV content missing for imported file. Please re-upload the file or use manual input.', type: 'error' }));
      return;
    }

    let csvContent = importCsv;
    let finalFileName = importCsvFileName;
    let mapping = { ...variableMapping };

    if (modeType === 'MANUAL') {
      const varNames = [...headerVariables, ...bodyVariables, ...buttonVariables.map(v => v.key)];
      varNames.forEach((v) => {
        mapping[v] = v;
      });
      mapping["receiver_phone_number"] = "receiver_phone_number";
      if (isAuthCopyCode) {
        mapping["code"] = "code";
      }
      csvContent = buildCsv(manualPhones, templateParams, isAuthCopyCode);
      finalFileName = `manual-${Date.now()}.csv`;
    } else if (modeType === 'IMPORT_CSV') {
      mapping = { ...mapping, receiver_phone_number: phoneColumn };
      if (finalFileName && /\.(xls|xlsx)$/i.test(finalFileName)) {
        finalFileName = finalFileName.replace(/\.(xls|xlsx)$/i, '.csv');
      }
    }

    if (isCampaignMode) {
      dispatch(createCampaignScheduleRequest({
        csv: csvContent,
        fileName: finalFileName,
        variableMapping: mapping,
        templateId: selectedTemplateId,
        executionMode,
        scheduledAt,
        campaignName,
      }));
    } else {
      const payload = {
        csv: csvContent,
        fileName: finalFileName,
        variableMapping: mapping,
        templateId: selectedTemplateId,
        executionMode: 'NOW'
      };
      dispatch(createCampaignRequest(payload));
    }
  };

  const handleVariableMappingChange = useCallback((variable, val) => {
    dispatch(setVariableMapping({ [variable]: val }));
  }, [dispatch]);

  const handleImportCsv = (phones, csvContent, fileName, source, columns, data, phoneCol) => {
    dispatch(setImportData({ recipients: phones, source: source, csv: csvContent, fileName }));
    if (columns) dispatch(setCsvColumns(columns));
    if (data) dispatch(setCsvData(data));
    if (phoneCol) dispatch(setPhoneColumn(phoneCol));
  };

  if (isSuccess) {
    return (
      <div className={waShell(whatsappPageShell.hybrid, "flex flex-col items-center justify-center text-center animate-fade-in")}>
        <div className="h-20 w-20 bg-[hsl(158,48%,36%)]/10 rounded-full flex items-center justify-center mb-6">
          <CheckCircle2 className="h-10 w-10 text-[hsl(158,45%,55%)]" />
        </div>
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
          {isCampaignMode
            ? (scheduleMode === "later" ? "Campaign Scheduled Successfully!" : "Campaign Created Successfully!")
            : "Message Processing Started!"
          }
        </h2>
        <p className="text-gray-500 dark:text-gray-400 mb-6">
          {isCampaignMode
            ? (scheduleMode === "later" ? "Your campaign will be sent at the scheduled time." : "Your campaign is now being processed.")
            : "Your message is now being processed."
          }
        </p>
        <Button 
          className="hover:cursor-pointer text-gray-800 dark:text-white" 
          onClick={() => {
            if (isCampaignMode) {
              navigate("/whatsapp/campaigns");
            } else {
              window.location.reload();
            }
          }}
        >
          {isCampaignMode ? "View All Campaigns" : "Send Another"}
        </Button>
      </div>
    );
  }

  return (
    <div className={waShell(whatsappPageShell.hybrid, "relative animate-fade-in")}>
      <WhatsAppPageIntro
        description={
          isCampaignMode
            ? "Configure recipients, template, and schedule for your campaign."
            : "Send a WhatsApp template or session message to your contacts."
        }
        className="mb-6"
      />
      {isCampaignMode && (
        <button
          onClick={() => navigate("/whatsapp/campaigns")}
          className="flex items-center gap-2 text-sm text-slate-500 hover:text-slate-900 dark:hover:text-white mb-6 transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Campaigns
        </button>
      )}

      {!hasWabaId && <WhatsAppConnectionNotice />}
      {shouldLockPage && <SubscriptionNotice message={!hasSendMessageAccess ? "You don't have access to the send message feature. Upgrade your plan to send messages." : "Your plan has expired or you don't have an active plan. Upgrade to send messages."} />}
      
      <div className={`grid grid-cols-1 lg:grid-cols-5 gap-8 animate-fade-in-up ${(!hasWabaId || shouldLockPage) ? 'pointer-events-none opacity-50' : ''}`}>
        <div className="lg:col-span-3 space-y-6">
          {/* Campaign details card */}
          {isCampaignMode && (
            <Card className="p-6 border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900">
              <div className="flex items-center gap-2 mb-4">
                <div className="h-8 w-8 rounded-full bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center text-purple-600 dark:text-purple-400 font-semibold text-sm">
                  <Zap className="h-4 w-4" />
                </div>
                <h3 className="font-semibold text-gray-900 dark:text-white">
                  Campaign Details
                </h3>
              </div>
              <div>
                <label className="text-gray-600 dark:text-slate-400 text-sm">
                  Campaign Name
                </label>
                <input
                  placeholder="e.g., Diwali Festival Sale 2026"
                  className="mt-1.5 h-12 w-full px-3 py-2 bg-gray-50 dark:bg-slate-950 border border-gray-200 dark:border-slate-800 focus:bg-white dark:focus:bg-slate-900 rounded-md text-sm focus:ring-2 focus:ring-[hsl(158,48%,36%)] text-gray-900 dark:text-white focus:outline-none"
                  value={campaignName}
                  onChange={(e) => setCampaignName(e.target.value)}
                />
                <p className="text-xs text-gray-500 dark:text-slate-500 mt-1.5">
                  Give your campaign a memorable name for easy tracking
                </p>
              </div>
            </Card>
          )}

          <RecipientInput
            manualNumbers={manualNumbers}
            onManualChange={(val) => dispatch(setManualNumbers(val))}
            importedCount={importedRecipients.length}
            importSource={importSource}
            onImportCsv={handleImportCsv}
            onClearImport={() => dispatch(clearImport())}
            templateVariables={allVariables}
            selectedTemplate={selectedTemplate}
          />

          {/* Info message for manual entry */}
          {manualNumbers && manualNumbers.trim().length > 0 && (
            <Card className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800">
              <div className="flex gap-3">
                <div className="flex-shrink-0 pt-0.5">
                  <svg className="h-5 w-5 text-blue-600 dark:text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                </div>
                <div>
                  <p className="text-sm text-blue-800 dark:text-blue-200">
                    <span className="font-semibold">Note:</span> When sending manually, you can only send to <strong>one recipient</strong>. To send to multiple recipients, please upload a CSV or Excel file.
                  </p>
                </div>
              </div>
            </Card>
          )}

          <TemplateSelector
            templates={templates}
            selectedId={selectedTemplateId}
            onSelect={(id) => dispatch(setTemplate(id))}
          />

          {/* Template Media Uploader (for image/video/document headers) */}
          {needsMediaUpload && selectedTemplateId && (
            <SendTemplateMediaUploader headerType={headerType} />
          )}

          {/* Template Variables */}
          {allVariables.length > 0 && (
            <Card className="p-6 border-gray-200 dark:border-slate-800 bg-white dark:bg-slate-900">
              <h3 className="font-semibold text-gray-900 dark:text-white mb-4">Template Variables</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <VariableMappingFields
                  isImportingCsvExcel={isImportingCsvExcel}
                  headerVariables={headerVariables}
                  bodyVariables={bodyVariables}
                  buttonVariables={buttonVariables}
                  isAuthCopyCode={isAuthCopyCode}
                  csvColumns={csvColumns}
                  variableMapping={variableMapping}
                  templateParams={templateParams}
                  handleVariableMappingChange={handleVariableMappingChange}
                  dispatch={dispatch}
                  setTemplateParams={setTemplateParams}
                />
              </div>
            </Card>
          )}

          {/* Scheduling Card */}
          {isCampaignMode && (
            <Card className="p-6 border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900">
              <div className="flex items-center gap-2 mb-4">
                <div className="h-8 w-8 rounded-full bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center text-orange-600 dark:text-orange-400 font-semibold text-sm">
                  <Clock className="h-4 w-4" />
                </div>
                <h3 className="font-semibold text-gray-900 dark:text-white">
                  Schedule Delivery
                </h3>
              </div>

              <div className="space-y-4">
                <div className="flex gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="scheduleMode"
                      value="now"
                      checked={scheduleMode === "now"}
                      onChange={() => setScheduleMode("now")}
                      className="text-[hsl(158,45%,55%)] focus:ring-[hsl(158,48%,36%)] h-4 w-4 accent-emerald-500"
                    />
                    <span className="text-sm font-medium text-gray-700 dark:text-slate-300">
                      Send Now
                    </span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="scheduleMode"
                      value="later"
                      checked={scheduleMode === "later"}
                      onChange={() => setScheduleMode("later")}
                      className="text-[hsl(158,45%,55%)] focus:ring-[hsl(158,48%,36%)] h-4 w-4 accent-emerald-500"
                    />
                    <span className="text-sm font-medium text-gray-700 dark:text-slate-300">
                      Schedule for Later
                    </span>
                  </label>
                </div>

                {scheduleMode === "later" && (
                  <div className="grid grid-cols-2 gap-4 mt-4">
                    <div>
                      <label className="text-gray-600 dark:text-slate-400 text-sm mb-1.5 flex items-center gap-1.5">
                        <Calendar className="h-4 w-4" /> Date
                      </label>
                      <input
                        type="date"
                        value={scheduleDate}
                        onChange={(e) => setScheduleDate(e.target.value)}
                        className="w-full px-3 py-2 bg-gray-50 dark:bg-slate-950 border border-gray-200 dark:border-slate-800 focus:bg-white dark:focus:bg-slate-900 rounded-md text-sm focus:ring-2 focus:ring-[hsl(158,48%,36%)] text-gray-900 dark:text-white focus:outline-none"
                        min={new Date().toISOString().split("T")[0]}
                      />
                    </div>
                    <div>
                      <label className="text-gray-600 dark:text-slate-400 text-sm mb-1.5 flex items-center gap-1.5">
                        <Clock className="h-4 w-4" /> Time
                      </label>
                      <input
                        type="time"
                        value={scheduleTime}
                        onChange={(e) => setScheduleTime(e.target.value)}
                        className="w-full px-3 py-2 bg-gray-50 dark:bg-slate-950 border border-gray-200 dark:border-slate-800 focus:bg-white dark:focus:bg-slate-900 rounded-md text-sm focus:ring-2 focus:ring-[hsl(158,48%,36%)] text-gray-900 dark:text-white focus:outline-none"
                      />
                    </div>
                  </div>
                )}
              </div>
            </Card>
          )}

          {selectedTemplateId && selectedTemplate?.status && selectedTemplate.status.toLowerCase() !== 'approved' && (
            <div className="p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg text-sm text-amber-700 dark:text-amber-400">
              ⚠️ Cannot send messages. Your template is currently {selectedTemplate.status.toLowerCase()}. Templates must be approved before sending messages.
            </div>
          )}

          {/* Action buttons and summaries */}
          {isCampaignMode ? (
            <div className="space-y-4">
              {totalRecipients > 0 && (
                <div className="bg-gray-100 dark:bg-slate-900 rounded-xl p-4 border border-slate-200 dark:border-slate-800">
                  <p className="text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">Sending to:</p>
                  <div className="space-y-2">
                    {importedRecipients.length > 0 && importSource && (
                      <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-slate-400">
                        <FileSpreadsheet className="h-4 w-4 text-orange-500" />
                        {importedRecipients.length} from {importSource}
                      </div>
                    )}
                    {manualCount > 0 && (
                      <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-slate-400">
                        <Phone className="h-4 w-4 text-gray-400" />
                        {manualCount} {manualCount === 1 ? "number" : "numbers"} entered manually
                      </div>
                    )}
                  </div>
                </div>
              )}
              
              <Button
                onClick={handleSend}
                disabled={!isFormValid || isSending}
                className="w-full h-12 bg-[hsl(158,48%,36%)] hover:bg-[hsl(158,48%,30%)] text-white font-semibold shadow-lg shadow-[hsl(158,48%,36%)]/20"
              >
                {isSending ? (
                  <span className="flex items-center gap-2 justify-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current" />
                    {scheduleMode === "later" ? "Scheduling..." : "Creating..."}
                  </span>
                ) : (
                  <span className="flex items-center gap-2 justify-center">
                    {scheduleMode === "later" ? <Clock className="h-5 w-5" /> : <Send className="h-5 w-5" />}
                    {scheduleMode === "later" ? "Schedule Campaign" : "Send Campaign Now"}
                  </span>
                )}
              </Button>
            </div>
          ) : (
            <Button
              onClick={handleSend}
              disabled={!isFormValid || isSending}
              className="w-full h-12 bg-[hsl(158,48%,36%)] hover:bg-[hsl(158,48%,30%)] text-white font-semibold text-lg shadow-lg shadow-[hsl(158,48%,36%)]/20"
            >
              {isSending ? (
                "Sending..."
              ) : (
                <span className="flex items-center gap-2 justify-center">
                  <Send className="h-5 w-5" />
                  Send to {totalRecipients} Recipients
                </span>
              )}
            </Button>
          )}
        </div>

        {/* Right Column: Preview only */}
        <div className="lg:col-span-2">
          <div className="sticky top-6">
            <TemplatePreview data={{
              headerType: headerType || 'none',
              headerText: selectedTemplate?.headerText || '',
              bodyText: selectedTemplate?.body || '',
              footerText: selectedTemplate?.footerText || '',
              buttons: selectedTemplate?.buttons || [],
              sampleValues: { ...templateParams.header, ...templateParams.body, ...templateParams.button, ...(isAuthCopyCode ? { code: templateParams.code } : {}) },
              mediaType: sendMediaFile ? headerType : 'none',
              mediaPreview: sendMediaFile?.preview || '',
              components: selectedTemplate?.components || null,
              isAuthTemplate: String(selectedTemplate?.category || '').toLowerCase() === 'authentication',
              otpType: selectedTemplate?.otpType || 'copy_code',
              otpButtonText: selectedTemplate?.otpButtonText || '',
              codeExpirationMinutes: selectedTemplate?.codeExpirationMinutes || 10,
              addSecurityRecommendation: !!selectedTemplate?.addSecurityRecommendation,
            }}
              mode="send" />
          </div>
        </div>
      </div>
    </div>
  );
}